/**
 * @file cookie-consent.service.ts
 * @description Persists user cookie-consent decisions in browser storage.
 *
 * Ported from apps/web-console (PrimeNG-free). Storage keys are kept in
 * sync with the web-console service so returning users don't see the
 * banner reappear across the two apps.
 *
 * @see apps/web-console/src/app/shared/services/cookie-consent.service.ts
 */
import { Injectable, computed, effect, signal } from '@angular/core';
import {
  getScopedStorageItem,
  removeScopedStorageItem,
  setScopedStorageItem,
} from '@kirigami/platform/browser';

export type CookieCategory = 'functional' | 'statistics' | 'marketing';

export interface CookieConsent {
  /** Bumped when categories or wording change so old choices can be invalidated. */
  version: number;
  givenAt: string;
  choices: Record<CookieCategory, boolean>;
}

export interface CookieDismissMeta {
  dismissedAt: string;
}

const STORAGE_KEY = 'cookieConsent.v1';
const DISMISS_KEY = 'cookieConsent.dismissedMeta.v1';

@Injectable({ providedIn: 'root' })
export class CookieConsentService {
  private readonly internalConsent = signal<CookieConsent | null>(this.readConsent());
  readonly consent = computed(() => this.internalConsent());
  readonly hasConsent = computed(() => this.internalConsent() !== null);

  constructor() {
    effect(() => {
      const c = this.internalConsent();
      if (c) {
        try {
          setScopedStorageItem(STORAGE_KEY, JSON.stringify(c));
          removeScopedStorageItem(DISMISS_KEY);
        } catch {
          /* storage unavailable — silently ignore */
        }
      }
    });
  }

  getDismissMeta(): CookieDismissMeta | null {
    try {
      const raw = getScopedStorageItem(DISMISS_KEY);
      return raw ? (JSON.parse(raw) as CookieDismissMeta) : null;
    } catch {
      return null;
    }
  }

  saveConsent(choices: Record<CookieCategory, boolean>): void {
    const payload: CookieConsent = {
      version: 1,
      givenAt: new Date().toISOString(),
      choices,
    };
    this.internalConsent.set(payload);
  }

  acceptAll(): void {
    this.saveConsent({
      functional: true,
      statistics: true,
      marketing: true,
    });
  }

  dismiss(): void {
    try {
      const meta: CookieDismissMeta = { dismissedAt: new Date().toISOString() };
      setScopedStorageItem(DISMISS_KEY, JSON.stringify(meta));
    } catch {
      /* storage unavailable — silently ignore */
    }
  }

  clear(): void {
    try {
      removeScopedStorageItem(STORAGE_KEY);
      removeScopedStorageItem(DISMISS_KEY);
    } catch {
      /* storage unavailable — silently ignore */
    }
    this.internalConsent.set(null);
  }

  private readConsent(): CookieConsent | null {
    try {
      const raw = getScopedStorageItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as CookieConsent;
      if (typeof parsed.version !== 'number') return null;
      if (!parsed.choices) return null;
      return parsed;
    } catch {
      return null;
    }
  }
}
